import React, { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Collapse,
  IconButton,
  AppBar,
} from "@mui/material";
import {
  Dashboard,
  Description,
  ExpandLess,
  ExpandMore,
  Checklist,
  PlayCircleOutline,
  ReportProblem,
  Menu,
  Close,
} from "@mui/icons-material";
import { GISTMContent } from "./GISTM_content";
import { GISTMAderencia } from "./GISTM_aderencia";
import { GISTMChecklist } from "./GISTM_checklist_page";
import { useParams } from "react-router-dom";
import { useApi } from "../../services";

const drawerWidth = 260;

interface MenuItem {
  label: string;
  icon: JSX.Element;
  key: string;
  children?: {
    key: string;
    label: string;
    icon?: JSX.Element;
  }[];
}

const menuItems: MenuItem[] = [
  {
    label: "APRESENTAÇÃO",
    icon: <PlayCircleOutline />,
    key: "gismt",
  },
  {
    label: "DASHBOARD",
    icon: <Dashboard />,
    key: "dashboard",
    children: [
      {
        label: "Visualizador de Aderência",
        key: "dash_aderencia",
        icon: <ReportProblem />,
      },
      {
        label: "Checklist de Ações",
        key: "dash_check",
        icon: <Checklist />
      },
    ],
  },
  {
    label: "REPOSITÓRIO",
    icon: <Description />,
    key: "repositorio",
  },
];

export const GISTMPage: React.FC = () => {
  const api = useApi();
  const { barragem } = useParams<{ barragem: string; }>();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [selected, setSelected] = useState<string>("gismt");
  const [urlRepositorioGISTM, setUrlRepositorioGISTM] = useState<string>();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    api.barragem.getBarragemById(barragem ? parseInt(barragem) : 0).then(response => {
      if ("error" in response) return;
      setUrlRepositorioGISTM(response.urlRepositorioGISTM);
    });
  }, [barragem]);

  const handleToggle = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelect = (key: string) => {
    if (key === "repositorio" && urlRepositorioGISTM) {
      const url = (urlRepositorioGISTM.startsWith("https://") || urlRepositorioGISTM.startsWith("http://"))
        ? urlRepositorioGISTM
        : `https://${urlRepositorioGISTM}`;
      window.open(url, "_blank");
      return;
    } else {
      setSelected(key);
    }
    setMobileOpen(false);
  };

  const drawerContent = (
    <Box>
      <Toolbar>
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
          GISTM
        </Typography>
        <IconButton onClick={() => setMobileOpen(false)} sx={{ display: { sm: "none" } }}>
          <Close />
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.key}>
            <ListItemButton
              onClick={() =>
                item.children ? handleToggle(item.key) : handleSelect(item.key)
              }
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              {item.children ? (
                openSections[item.key] ? <ExpandLess /> : <ExpandMore />
              ) : null}
            </ListItemButton>
            {item.children && (
              <Collapse in={openSections[item.key]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((sub) => (
                    <ListItemButton
                      key={sub.key}
                      sx={{ pl: 4 }}
                      selected={selected === sub.key}
                      onClick={() => handleSelect(sub.key)}
                    >
                      <ListItemIcon>{sub.icon}</ListItemIcon>
                      <ListItemText primary={sub.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* AppBar para mobile */}
      <AppBar
        position="fixed"
        sx={{
          display: { sm: "none" },
          backgroundColor: "background.paper",
          color: "text.primary",
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2 }}
          >
            <Menu />
          </IconButton>
          <Typography variant="h6" noWrap>
            Estruturas
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Drawer móvel */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Box fixo para PC */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          flexDirection: "column",
          width: drawerWidth,
          flexShrink: 0,
          borderRight: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          overflowY: "auto",
        }}
      >
        {drawerContent}
      </Box>

      {/* Área principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: "background.default",
          height: "100vh",
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ display: { sm: "none" } }} />
        {selectScreen(selected)}
      </Box>
    </Box>
  );
};

const selectScreen = (screen: string) => {
  switch (screen) {
    case "gistm":
      return <GISTMContent />;
    case "dash_aderencia":
      return <GISTMAderencia />;
    case "dash_check":
      return <GISTMChecklist />;
    default:
      return <GISTMContent />;
  }
};
